import config from '@/config.js'
import { getStatic } from '@/services/data-source.js'
import { loadStaticLemmaRows } from '@/services/lemma-list.js'
import {
  compileWordFormPattern,
  searchableWordForm,
} from '@/services/word-form-search.js'

const FUZZY_SEARCH_SCHEMA_VERSION = 1
const FUZZY_SEARCH_FIELDS = [
  'match',
  'entity',
  'lemmaId',
  'lemma',
  'language',
  'articleId',
  'article',
  'dictionary',
  'dictionaryId',
]
const FUZZY_SEARCH_ENTITIES = new Set(['internal', 'external', 'lemma'])
const FUZZY_FIELD_INDEX = Object.fromEntries(
  FUZZY_SEARCH_FIELDS.map((field, index) => [field, index])
)

let fuzzyIndexPromise = null
let fuzzyIndexBaseUrl = null
let fuzzyMissingBaseUrl = null

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function normalizeFuzzySearchRow(rawRow, index) {
  let row = rawRow
  if (Array.isArray(row) && row.length === 1) row = row[0]
  if (!isObject(row)) {
    throw new Error(`Lemma fuzzy-search row ${index} must be an object`)
  }
  if (
    row.lemmaId === null ||
    row.lemmaId === undefined ||
    typeof row.lemma !== 'string' ||
    typeof row.match !== 'string' ||
    !FUZZY_SEARCH_ENTITIES.has(row.entity)
  ) {
    throw new Error(`Lemma fuzzy-search row ${index} is missing required display fields`)
  }

  return Object.freeze({
    match: row.match,
    entity: row.entity,
    lemmaId: String(row.lemmaId),
    lemma: row.lemma,
    language: row.language === null || row.language === undefined
      ? null
      : String(row.language),
    articleId: row.articleId === null || row.articleId === undefined
      ? null
      : String(row.articleId),
    article: row.article === null || row.article === undefined
      ? null
      : String(row.article),
    dictionary: row.dictionary === null || row.dictionary === undefined
      ? null
      : String(row.dictionary),
    dictionaryId: row.dictionaryId === null || row.dictionaryId === undefined
      ? null
      : String(row.dictionaryId),
  })
}

function normalizeFuzzySearchPayload(payload) {
  let rows
  if (Array.isArray(payload)) {
    rows = payload
  } else {
    if (!isObject(payload) || payload.schemaVersion !== FUZZY_SEARCH_SCHEMA_VERSION) {
      throw new Error(`Unsupported lemma fuzzy-search schema version: ${payload?.schemaVersion}`)
    }
    if (
      !Array.isArray(payload.fields) ||
      payload.fields.length !== FUZZY_SEARCH_FIELDS.length ||
      payload.fields.some((field, index) => field !== FUZZY_SEARCH_FIELDS[index]) ||
      !Array.isArray(payload.rows)
    ) {
      throw new Error('Lemma fuzzy-search fields do not match the expected schema')
    }
    rows = payload.rows.map((values, index) => {
      if (!Array.isArray(values) || values.length !== FUZZY_SEARCH_FIELDS.length) {
        throw new Error(`Lemma fuzzy-search row ${index} has an invalid field count`)
      }
      return Object.fromEntries(
        FUZZY_SEARCH_FIELDS.map((field, fieldIndex) => [field, values[fieldIndex]])
      )
    })
  }
  return Object.freeze(rows.map(normalizeFuzzySearchRow))
}

function normalizeStaticFuzzySearchPayload(payload) {
  if (Array.isArray(payload)) {
    return {
      compact: false,
      rows: normalizeFuzzySearchPayload(payload),
    }
  }
  if (!isObject(payload) || payload.schemaVersion !== FUZZY_SEARCH_SCHEMA_VERSION) {
    throw new Error(`Unsupported lemma fuzzy-search schema version: ${payload?.schemaVersion}`)
  }
  if (
    !Array.isArray(payload.fields) ||
    payload.fields.length !== FUZZY_SEARCH_FIELDS.length ||
    payload.fields.some((field, index) => field !== FUZZY_SEARCH_FIELDS[index]) ||
    !Array.isArray(payload.rows)
  ) {
    throw new Error('Lemma fuzzy-search fields do not match the expected schema')
  }
  payload.rows.forEach((values, index) => {
    if (!Array.isArray(values) || values.length !== FUZZY_SEARCH_FIELDS.length) {
      throw new Error(`Lemma fuzzy-search row ${index} has an invalid field count`)
    }
    if (
      typeof values[FUZZY_FIELD_INDEX.match] !== 'string' ||
      !FUZZY_SEARCH_ENTITIES.has(values[FUZZY_FIELD_INDEX.entity]) ||
      values[FUZZY_FIELD_INDEX.lemmaId] === null ||
      values[FUZZY_FIELD_INDEX.lemmaId] === undefined ||
      typeof values[FUZZY_FIELD_INDEX.lemma] !== 'string'
    ) {
      throw new Error(`Lemma fuzzy-search row ${index} is missing required display fields`)
    }
  })
  return {
    compact: true,
    rows: Object.freeze(payload.rows),
  }
}

function selectedValues(values) {
  return new Set((values || []).map(value => String(value)))
}

function sortFuzzySearchRows(rows) {
  return rows.map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const lemmaOrder = searchableWordForm(a.row.lemma)
        .localeCompare(searchableWordForm(b.row.lemma))
      if (lemmaOrder) return lemmaOrder
      const matchOrder = searchableWordForm(a.row.match)
        .localeCompare(searchableWordForm(b.row.match))
      if (matchOrder) return matchOrder
      const entityOrder = a.row.entity.localeCompare(b.row.entity)
      return entityOrder || a.row.lemmaId.localeCompare(b.row.lemmaId) || a.index - b.index
    })
    .map(item => item.row)
}

function filterFuzzySearchRows(rows, criteria) {
  const expression = compileWordFormPattern(criteria.pattern)
  if (!expression) return []

  const entities = selectedValues(criteria.filters)
  const languages = selectedValues(
    (criteria.languages || []).map(
      language => isObject(language) ? language.code : language
    )
  )
  const dictionaryIds = selectedValues(criteria.userDictionaries)
  const dictionaryAcronyms = selectedValues(criteria.dictionaryAcronyms)

  if (!entities.size) return []

  const matches = rows.filter(row => {
    if (!entities.has(row.entity)) return false
    if (languages.size && !languages.has(String(row.language))) return false
    if (
      (dictionaryIds.size || dictionaryAcronyms.size) &&
      !dictionaryIds.has(String(row.dictionaryId)) &&
      !dictionaryAcronyms.has(String(row.dictionary))
    ) {
      return false
    }
    return expression.test(searchableWordForm(row.match))
  })

  return sortFuzzySearchRows(matches)
}

function filterCompactFuzzySearchRows(rows, criteria) {
  const expression = compileWordFormPattern(criteria.pattern)
  if (!expression) return []
  const entities = selectedValues(criteria.filters)
  const languages = selectedValues(
    (criteria.languages || []).map(
      language => isObject(language) ? language.code : language
    )
  )
  const dictionaryIds = selectedValues(criteria.userDictionaries)
  const dictionaryAcronyms = selectedValues(criteria.dictionaryAcronyms)
  if (!entities.size) return []

  const matches = []
  rows.forEach((values, index) => {
    if (!entities.has(values[FUZZY_FIELD_INDEX.entity])) return
    if (
      languages.size &&
      !languages.has(String(values[FUZZY_FIELD_INDEX.language]))
    ) {
      return
    }
    if (
      (dictionaryIds.size || dictionaryAcronyms.size) &&
      !dictionaryIds.has(String(values[FUZZY_FIELD_INDEX.dictionaryId])) &&
      !dictionaryAcronyms.has(String(values[FUZZY_FIELD_INDEX.dictionary]))
    ) {
      return
    }
    if (!expression.test(searchableWordForm(values[FUZZY_FIELD_INDEX.match]))) return
    matches.push(normalizeFuzzySearchRow(
      Object.fromEntries(
        FUZZY_SEARCH_FIELDS.map((field, fieldIndex) => [field, values[fieldIndex]])
      ),
      index
    ))
  })
  return sortFuzzySearchRows(matches)
}

function loadStaticFuzzySearchIndex() {
  const baseUrl = String(config.apiUrlClientSide || '')
  if (!fuzzyIndexPromise || fuzzyIndexBaseUrl !== baseUrl) {
    fuzzyIndexBaseUrl = baseUrl
    fuzzyIndexPromise = getStatic('lemma_fuzzy_search/index.json')
      .then(response => {
        const payload = response.data
        if (
          !isObject(payload) ||
          payload.schemaVersion !== FUZZY_SEARCH_SCHEMA_VERSION ||
          !isObject(payload.entities) ||
          [...FUZZY_SEARCH_ENTITIES].some(entity => (
            !Number.isInteger(payload.entities[entity]) ||
            payload.entities[entity] < 0
          ))
        ) {
          throw new Error('Lemma fuzzy-search index is malformed')
        }
        return Object.freeze(payload)
      })
      .catch(error => {
        fuzzyIndexPromise = null
        fuzzyIndexBaseUrl = null
        if (error.response && error.response.status === 404) {
          error.code = 'STATIC_LEMMA_FUZZY_INDEX_NOT_FOUND'
        }
        throw error
      })
  }
  return fuzzyIndexPromise
}

async function loadStaticFuzzySearchRows(entities) {
  await loadStaticFuzzySearchIndex()
  return Promise.all(
    [...entities].sort().map(entity => (
      getStatic(`lemma_fuzzy_search/${entity}.json`)
        .then(response => normalizeStaticFuzzySearchPayload(response.data))
    ))
  )
}

function fallbackLemmaRow(row) {
  return normalizeFuzzySearchRow({
    match: row.lemma,
    entity: 'lemma',
    lemmaId: row.lemmaId,
    lemma: row.lemma,
    language: row.lemmaLanguageCode,
    articleId: row.articleId,
    article: row.articleLemma,
    dictionary: row.dictionary,
    dictionaryId: null,
  }, row.lemmaId)
}

async function queryLegacyLemmaOnly(criteria) {
  const entities = selectedValues(criteria.filters)
  if (!entities.has('lemma')) {
    const error = new Error(
      'The static export does not contain internal/external lemma-search data. ' +
      'Regenerate the lemma_fuzzy_search export to search the selected entities.'
    )
    error.code = 'STATIC_LEMMA_FUZZY_EXPORT_REQUIRED'
    throw error
  }

  const expression = compileWordFormPattern(criteria.pattern)
  const languages = selectedValues(
    (criteria.languages || []).map(
      language => isObject(language) ? language.code : language
    )
  )
  const dictionaryAcronyms = selectedValues(criteria.dictionaryAcronyms)
  const seen = new Set()
  const matches = []
  const sourceRows = await loadStaticLemmaRows()
  sourceRows.forEach(row => {
    if (languages.size && !languages.has(String(row.lemmaLanguageCode))) return
    if (
      dictionaryAcronyms.size &&
      !dictionaryAcronyms.has(String(row.dictionary))
    ) {
      return
    }
    if (!expression.test(searchableWordForm(row.lemma))) return
    const normalized = fallbackLemmaRow(row)
    const key = [
      normalized.entity,
      normalized.match,
      normalized.lemmaId,
      normalized.articleId,
    ].join('\u0000')
    if (!seen.has(key)) {
      seen.add(key)
      matches.push(normalized)
    }
  })

  return filterFuzzySearchRows(matches, {
    ...criteria,
    filters: ['lemma'],
  })
}

async function queryStaticFuzzySearch(criteria) {
  if (!compileWordFormPattern(criteria.pattern) || !(criteria.filters || []).length) {
    return { data: [], count: 0, legacyFallback: false }
  }
  const baseUrl = String(config.apiUrlClientSide || '')
  if (fuzzyMissingBaseUrl === baseUrl) {
    const data = await queryLegacyLemmaOnly(criteria)
    return { data, count: data.length, legacyFallback: true }
  }
  try {
    const entities = selectedValues(criteria.filters)
    if ([...entities].some(entity => !FUZZY_SEARCH_ENTITIES.has(entity))) {
      throw new Error('Lemma fuzzy-search contains an unsupported entity filter')
    }
    const payloads = await loadStaticFuzzySearchRows(entities)
    const data = sortFuzzySearchRows(payloads.flatMap(payload => (
      payload.compact
        ? filterCompactFuzzySearchRows(payload.rows, criteria)
        : filterFuzzySearchRows(payload.rows, criteria)
    )))
    return { data, count: data.length, legacyFallback: false }
  } catch (error) {
    if (error.code !== 'STATIC_LEMMA_FUZZY_INDEX_NOT_FOUND') throw error
    fuzzyMissingBaseUrl = baseUrl
    const data = await queryLegacyLemmaOnly(criteria)
    return { data, count: data.length, legacyFallback: true }
  }
}

function clearStaticFuzzySearchCache() {
  fuzzyIndexPromise = null
  fuzzyIndexBaseUrl = null
  fuzzyMissingBaseUrl = null
}

export {
  FUZZY_SEARCH_FIELDS,
  clearStaticFuzzySearchCache,
  filterFuzzySearchRows,
  normalizeFuzzySearchPayload,
  normalizeStaticFuzzySearchPayload,
  queryStaticFuzzySearch,
}
